import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { ContentMode } from '@/types';

interface ToastPayload {
  title: string;
  message: string;
}

interface UiState {
  mode: ContentMode;
  navScrolled: boolean;
  toastVisible: boolean;
  toastTitle: string;
  toastMessage: string;
  transcriptSearch: string;
  passageSearch: string;
  uploadModalOpen: boolean;
  uploadInProgress: boolean;
  selectedTileIds: string[];
  contentEnrichmentOpen: boolean;
}

const initialState: UiState = {
  mode: 'media',
  navScrolled: false,
  toastVisible: false,
  toastTitle: '',
  toastMessage: '',
  transcriptSearch: '',
  passageSearch: '',
  uploadModalOpen: false,
  uploadInProgress: false,
  selectedTileIds: [],
  contentEnrichmentOpen: false,
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setMode(state, action: PayloadAction<ContentMode>) {
      state.mode = action.payload;
    },
    setNavScrolled(state, action: PayloadAction<boolean>) {
      state.navScrolled = action.payload;
    },
    showToast(state, action: PayloadAction<ToastPayload>) {
      state.toastVisible = true;
      state.toastTitle = action.payload.title;
      state.toastMessage = action.payload.message;
    },
    hideToast(state) {
      state.toastVisible = false;
    },
    setTranscriptSearch(state, action: PayloadAction<string>) {
      state.transcriptSearch = action.payload;
    },
    setPassageSearch(state, action: PayloadAction<string>) {
      state.passageSearch = action.payload;
    },
    openUploadModal(state) {
      state.uploadModalOpen = true;
    },
    closeUploadModal(state) {
      state.uploadModalOpen = false;
    },
    setUploadInProgress(state, action: PayloadAction<boolean>) {
      state.uploadInProgress = action.payload;
    },
    toggleTileSelection(state, action: PayloadAction<string>) {
      const id = action.payload;
      const idx = state.selectedTileIds.indexOf(id);
      if (idx === -1) {
        state.selectedTileIds.push(id);
      } else {
        state.selectedTileIds.splice(idx, 1);
      }
    },
    clearTileSelection(state) {
      state.selectedTileIds = [];
    },
    openContentEnrichment(state) {
      state.contentEnrichmentOpen = true;
    },
    closeContentEnrichment(state) {
      state.contentEnrichmentOpen = false;
    },
  },
});

export const {
  setMode,
  setNavScrolled,
  showToast,
  hideToast,
  setTranscriptSearch,
  setPassageSearch,
  openUploadModal,
  closeUploadModal,
  setUploadInProgress,
  toggleTileSelection,
  clearTileSelection,
  openContentEnrichment,
  closeContentEnrichment,
} = uiSlice.actions;
export default uiSlice.reducer;
